import React, {useEffect} from "react";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import {generateNewTickets} from "../../actions/ticket";
import Button from "../../common/button/button";
import InputFieldWithLabel from "../../common/input/inputFieldWithLabel";
import SelectWithLabel from "../../common/input/selectWithLabel";
import {
  createErrorNotification,
  createInfoNotification,
  createSuccessNotification,
} from "../../common/notifications/notification";
import {newModalStyle} from "../../reusable/styles";
import { ticketSeriesTypes } from "../../globals";
import { getLanguageOptions } from "../../utils/ticketSeriesSupportedLanguage";

const CH_PARKING = "CH_PARKING";
const DE_PARKING_SERIES_TYPE = "DE_PARKING_VIOLATION_NON_ZPO";
const DEFAULT_DE_ZONE_CHARGE = 35;

const ModalGenerateTickets = (props) => {
  const {
    handleSubmit,
    watch,
    register,
    reset,
    setValue,
    unregister,
      formState: {errors}
  } =
      useForm({
        defaultValues: {selectedLanguage: "0", selectedCountry: "0", ticketSeriesType: CH_PARKING},
        mode: "all",
      });
  const dispatch = useDispatch();
  const watchAmount = watch("amount");
  const watchLanguage = watch("selectedLanguage");
  const watchCountry = watch("selectedCountry");
  const ticketSeriesTypeWatch = watch("ticketSeriesType");
  const availableLanguages = getLanguageOptions(ticketSeriesTypeWatch);
  const isChParking = ticketSeriesTypeWatch === CH_PARKING;
  const isDeParkingSeries = ticketSeriesTypeWatch === DE_PARKING_SERIES_TYPE;
  const watchDeZoneCharge = watch("deZoneCharge");

  // The DE zone charge is only printed on DE parking tickets: reset it to the default when that type is chosen,
  // and drop it (value and validation) for every other type
  useEffect(() => {
    if (isDeParkingSeries) {
      setValue("deZoneCharge", DEFAULT_DE_ZONE_CHARGE);
    } else {
      unregister("deZoneCharge");
    }
  }, [isDeParkingSeries, setValue, unregister]);

  // Preselect the language of a single-language type, replacing one picked for the previous type
  const singleLanguage = availableLanguages.length === 1 ? availableLanguages[0] : null;
  useEffect(() => {
    if (singleLanguage) {
      setValue("selectedLanguage", singleLanguage);
    }
  }, [singleLanguage, setValue]);

  const { t } = useTranslation();


  const generate = (data) => {
    const successNotification = () =>
      createSuccessNotification(
        t("Ticket.CreatedSuccessfully"),
        t("notifications.success")
      );

    const errorNotification = () =>
      createErrorNotification(
        t("Ticket.CreatedUnsuccessfully"),
        t("notifications.error")
      );
    const infoNotification = () =>
      createInfoNotification(
        t("Ticket.CreatedPostponed"),
        t("notifications.info")
      );

    const createSeriesDto = {
      size: data.amount,
      country: data.selectedCountry,
      ticketSeriesType: ticketSeriesTypeWatch,
      deZoneCharge: isDeParkingSeries ? Number(data.deZoneCharge) : undefined
    };
    if (!isChParking) {
      createSeriesDto.paymentPeriod = data.paymentPeriod;
    }

    dispatch(
      generateNewTickets(
        createSeriesDto,
        successNotification,
        errorNotification,
        infoNotification,
        data.selectedLanguage
      )
    );
    props.close();
    reset();
  };

  const closeModal = () => {
    props.close();
    reset();
  };

  const submitDisabled =
    !watchAmount || watchLanguage === "0" || (isDeParkingSeries && !(Number(watchDeZoneCharge) > 0));
  
  return (
    <Modal
      open={props.open}
      closeOnEsc
      onClose={closeModal}
      center
      styles={newModalStyle({ width: "400px" })}
    >
      <div className="modal__headerLine" />
      <div className="modal__title">{t("Ticket.Generate Tickets Modal")}</div>
      <form className="modalForm" onSubmit={handleSubmit(generate)}>
        <div className="form">
          <InputFieldWithLabel
            label={t("Ticket.Amount")}
            type="number"
            min={1}
            max={1000}
            step="1"
            name="amount"
            registerName="amount"
            register={register}
            required
            validationProps={{
              required: t("Validation.required"),
              min: { value: 1, message: t("Validation.range", { min: 1, max: 1000 }) },
              max: { value: 1000, message: t("Validation.range", { min: 1, max: 1000 }) },
            }}
            errors={errors}
          />
          <SelectWithLabel
              label={t("Ticket.ticketSeriesType")}
              name="ticketSeriesType"
              registerName="ticketSeriesType"
              register={register}
              required={true}
              whiteBackground
              defaultValue={CH_PARKING}
              hideDefaultOption={true}
              options={ticketSeriesTypes.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
          />
          {isDeParkingSeries && (
            <InputFieldWithLabel
              label={t("Ticket.deZoneCharge")}
              type="number"
              min={0.01}
              step="0.01"
              name="deZoneCharge"
              registerName="deZoneCharge"
              defaultValue={DEFAULT_DE_ZONE_CHARGE}
              register={register}
              required
              validationProps={{
                required: t("Validation.required"),
                min: { value: 0.01, message: t("Validation.required") },
              }}
              errors={errors}
            />
          )}
          {!isChParking && (
            <InputFieldWithLabel
              label={t("Ticket.paymentPeriod")}
              name="paymentPeriod"
              registerName="paymentPeriod"
              defaultValue={14}
              register={register}
              required
            />
          )}
          <SelectWithLabel
            name="selectedLanguage"
            style={{ backgroundColor: "white" }}
            whiteBackground
            registerName="selectedLanguage"
            label={t("Ticket.SelectLangauge")}
            register={register}
            required={true}
            defaultOption={t("Ticket.SelectLangauge")}
            options={availableLanguages.map((language, index) => {
              return (
                <option key={index + 1} value={language}>
                  {language}
                </option>
              );
            })}
          />
        </div>

        <Button
            text={t("Ticket.Generate")}
            buttonStyle={{marginBottom: "20px", width: "100%"}}
            disabled={submitDisabled}
        />
      </form>
    </Modal>
  );
};
export default ModalGenerateTickets;
